import React, { useEffect, useState } from 'react';
import moment, { Moment } from 'moment';

import { useAppSelector } from '../app/hooks';

import { FormControl, InputLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import getDeviceTypeMobile from '../selectors/getDeviceTypeMobile';
import getThemeLookup from '../selectors/getThemeLookup';

//import getThemeLookup from '../selectors/getThemeLookup';

// ! Do not include any specific functionality for a specific flow, this is a primative
// ! component. We should be able to use this component in any circumstances as building blocks.

/**
 * # DateSelector
 * This is a primitive component for a date (only date) selector.
 *
 * @param onDateChange - When date is being set
 * @param dates - It's important that these dates should have the time set to 00:00:00
 * @returns
 */
const DateSelector = ({
  value,
  onDateChange,
  dates,
}: {
  value?: string;
  onDateChange: (value: Moment) => void;
  dates: Moment[];
}) => {
  const isMobile = useAppSelector(getDeviceTypeMobile);

  const changeDate = (selectedValue: string | Moment) => {
    const timeMoment = moment(value);
    const newDateWithTime = moment(selectedValue).set({
      hours: timeMoment.hours(),
      minutes: timeMoment.minutes(),
    });

    onDateChange(newDateWithTime);
  };

  useEffect(() => {
    if (!value) {
      const firstOptionFallback = dates[0];
      changeDate(firstOptionFallback);
    }
  }, []);

  // We do this so we can select the times selected in the dropdown.
  // The dropdown options purposefully has dates set without the times set
  const [dropdownValue, setDropdownValue] = useState<string>('');
  useEffect(() => {
    const iso = moment(value).set({ h: 0, m: 0, s: 0, ms: 0 }).toISOString();
    setDropdownValue(iso);
  }, [value]);
  const variant = isMobile ? 'standard' : 'outlined';
  const p = useAppSelector(getThemeLookup);

  return (
    <FormControl
      sx={{
        ...styles.dropdownWrapper,
        minWidth: isMobile ? 140 : 231,
        '& .MuiInputLabel-root.Mui-focused': {
          color: p('fieldHoverBorderColor', ['color']).color,
        },
        '& .MuiOutlinedInput-root': {
          '& fieldset': {
            borderColor: p('fieldDefaultBorder', ['color']).color,
          },
          '&:hover fieldset': {
            borderColor: p('fieldHoverBorderColor', ['color']).color,
          },
          '&.Mui-focused fieldset': {
            borderColor: p('fieldBorderFocus', ['color']).color,
          },
        },
      }}
    >
      <InputLabel id="date-select-label" variant={variant}>
        Date
      </InputLabel>
      <Select
        labelId="date-select-label"
        id="date-select"
        value={dropdownValue}
        label="Date"
        onChange={(event: SelectChangeEvent) => {
          const newDate = event.target.value;
          changeDate(newDate);
        }}
        variant={variant}
      >
        {dates.map(date => (
          <MenuItem key={date.toISOString()} value={date.toISOString()}>
            {date.format('ddd, D MMM, YY')}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

const styles: Styles = {
  dropdownWrapper: { flex: 1 },
};

export default DateSelector;
